import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useUnreadCount, markRead, markAllRead, notificationHref, CLASS_SHORT, STATE_LABEL } from '../lib/notifications.js';

function ago(iso) {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

/** One notification row (shared by the dropdown and the full page). */
export function NotificationRow({ n, onOpen }) {
  const a = n.alert;
  const trigger = a.value != null && a.metric !== 'weatherCode' && a.metric !== 'distanceKm' ? ` · ${a.value}${a.unit ? ` ${a.unit}` : ''}` : '';
  return (
    <button type="button" className={`notif-row sev-${a.severityRank || 'na'} state-${n.state}`} onClick={() => onOpen(n)}>
      <span className="notif-dot" aria-hidden="true" />
      <span className="notif-body">
        <span className="notif-title">
          {a.title} — {n.asset.name}
        </span>
        <span className="notif-meta">
          {a.severity} · {CLASS_SHORT[a.class] ?? a.class}
          {trigger} · {n.kind === 'escalated' ? 'escalated ' : ''}
          {ago(n.updatedAt)}
          {n.state !== 'new' ? ` · ${STATE_LABEL[n.state]}` : ''}
        </span>
      </span>
    </button>
  );
}

/**
 * Bell beside the account bubble: unread count (alerts still active), the
 * latest notifications, mark read, and links to the map and the full list.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useUnreadCount(true);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    setError(null);
    api('/notifications?limit=8')
      .then((r) => {
        setItems(r.items);
        setUnread(r.unread);
      })
      .catch((err) => setError(err.message));
    const close = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const esc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close, { passive: true });
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open, setUnread]);

  const openOne = async (n) => {
    setOpen(false);
    if (!n.read) markRead([n.id]).catch(() => {});
    navigate(notificationHref(n));
  };

  return (
    <div className="bell-wrap" ref={ref}>
      <button
        type="button"
        className="bell-btn"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"
          />
        </svg>
        {unread > 0 && <span className="bell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open &&
        createPortal(
          <div ref={popRef} className="notif-pop" role="dialog" aria-label="Notifications">
            <div className="notif-head">
              <strong>Notifications</strong>
              {unread > 0 && (
                <button
                  type="button"
                  className="btn-link"
                  onClick={async () => {
                    await markAllRead().catch(() => {});
                    setUnread(0);
                    setItems((list) => list?.map((x) => ({ ...x, read: true, state: x.state === 'new' ? 'read' : x.state })));
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>
            {error && <div className="acct-err">{error}</div>}
            {!items && !error && <div className="muted notif-empty">Loading…</div>}
            {items?.length === 0 && (
              <div className="muted notif-empty">No alerts on your sites or routes yet. You’ll be notified here when one is affected.</div>
            )}
            {items?.map((n) => (
              <NotificationRow key={n.id} n={n} onOpen={openOne} />
            ))}
            <div className="notif-foot">
              <Link to="/notifications" onClick={() => setOpen(false)}>
                View all notifications
              </Link>
              <Link to="/settings#notifications" onClick={() => setOpen(false)}>
                Settings
              </Link>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
